import { useState } from 'react';
import { message } from 'antd';
import { InventoryMeterInstaller } from 'services/inventoryMeterInstaller';
import { useServices } from 'services/useServices';
import { captureException } from '@sentry/react';
import { useIntl } from 'react-intl';
import { useScrollTopOnMount } from './useScrollTopOnMount';
import { useStore } from '../routes/installation/useStore';
import { customApiKeyAuthenticator } from '../services/httpClient';
import { InstallMeterRecordDb } from '../indexed_db/installMeterDb';
import { getInstallMeterDbApi } from '../api/indexed_db/installMeterDb';
import { SynchronizeStatus, SynchronizeStep } from '../consts/sync';
import { SynchronizeInstallationMeters as messages } from './messages';

const sendItem = async (
  inventoryMeterInstaller: InventoryMeterInstaller,
  { id, installationMeter, locationApiKey }: InstallMeterRecordDb,
) => {
  await inventoryMeterInstaller.install(installationMeter, {
    authenticator: customApiKeyAuthenticator(locationApiKey),
  });
  await getInstallMeterDbApi().setSyncedToRecord(id);
};

export interface SynchronizeInstallationMetersShape {
  syncStatus: SynchronizeStatus;
  syncOfflineRecords: () => void;
}

export const useSynchronizeInstallationMeters = (): SynchronizeInstallationMetersShape => {
  const { inventoryMeterInstaller } = useServices();
  const forceScrollTop = useScrollTopOnMount();
  const { queuedMetersCount, setQueuedMetersCount, setIsSendingData, reset } = useStore();
  const intl = useIntl();

  const [syncStatus, setSyncStatus] = useState<SynchronizeStatus>({
    syncStep: SynchronizeStep.Confirm,
    queuedCount: queuedMetersCount ?? 0,
    syncedCount: 0,
    rejectedCount: 0,
  });

  const synchronizeInventoryMeters = async (items: InstallMeterRecordDb[]) => {
    // we want to send meters sequentially
    for (const item of items) {
      try {
        await sendItem(inventoryMeterInstaller, item);
        setSyncStatus(prevState => ({
          ...prevState,
          syncStep: SynchronizeStep.InProgress,
          syncedCount: prevState.syncedCount + 1,
        }));
      } catch (err) {
        message.error(intl.formatMessage(messages.errorMessage, { meterId: item.installationMeter.inventoryMeterId }));
        console.error(err);
        captureException(err);
        setSyncStatus(prevState => ({
          ...prevState,
          syncStep: SynchronizeStep.InProgress,
          rejectedCount: prevState.rejectedCount + 1,
        }));
      }
    }
  };

  const syncOfflineRecords = async () => {
    setSyncStatus(prevState => ({ ...prevState, syncStep: SynchronizeStep.GetReady }));
    setIsSendingData(true);

    const installMetersData = await getInstallMeterDbApi().getData();
    await synchronizeInventoryMeters(installMetersData.filter(item => !item.wasSynced));

    setSyncStatus(prevState => ({ ...prevState, syncStep: SynchronizeStep.Finished }));

    // update queued meters count
    const installMeterDb = getInstallMeterDbApi();
    const getQueuedMetersCount = async () => {
      const metersCount = await installMeterDb.countNotSynchronized();
      setQueuedMetersCount(metersCount);
    };
    getQueuedMetersCount();

    setIsSendingData(false);
    reset();
    forceScrollTop();
  };

  return { syncStatus, syncOfflineRecords };
};
